/**
 * The metric table, read and never re-stated.
 *
 * `GET /api/v1/config` publishes the whole table per task type, with the
 * direction attached, and the server validates every metric against it. This
 * module only looks things up in what the server sent: a copy of the table
 * here would be a second place to keep the pairing right, and an F1 checked
 * against an AUC bar is exactly how a deal gets claimed on a number that never
 * existed.
 */

import type {
  ConsoleConfig,
  MetricOption,
  Purpose,
  TaskType,
} from "../api/types";

/**
 * The metrics a task type may use.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *     taskType: The dataset's task type.
 *
 * Returns:
 *     The options for that task type, or an empty list while the console has
 *     not published the table. An empty list is why a metric picker renders
 *     "the console has not published the metrics yet" instead of a guess.
 */
export function metricsFor(
  config: ConsoleConfig | undefined,
  taskType: TaskType,
): MetricOption[] {
  return config?.metrics?.[taskType] ?? [];
}

/**
 * What a metric is called on screen.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *     taskType: The dataset's task type.
 *     metric: The metric slug on the record.
 *
 * Returns:
 *     The published label, or the slug itself when the table has not arrived —
 *     the slug is what the server called it, and inventing a prettier name for
 *     a metric nobody published would be inventing a fact.
 */
export function metricLabel(
  config: ConsoleConfig | undefined,
  taskType: TaskType,
  metric: string | null,
): string {
  if (!metric) {
    return "";
  }
  const found = metricsFor(config, taskType).find(
    (option) => option.metric === metric,
  );
  return found?.label ?? metric;
}

/** What each of the three task types is called on screen. */
export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  classification: "Classification",
  regression: "Regression",
  time_series: "Time series",
};

/** One line saying what each task type means, in a GTM person's words. */
export const TASK_TYPE_HINTS: Record<TaskType, string> = {
  classification: "Which class is it — churn, fraud, a category.",
  regression: "How much is it — a price, a quantity, a score.",
  time_series: "What comes next — demand, volume, a value over time.",
};

/** The sentence a client agreed to, for each of the three permissions. */
export const PURPOSE_SENTENCES: Record<Purpose, string> = {
  benchmark: "Neuralk may measure models against this data",
  training: "Neuralk may train on it",
  exploration: "Neuralk may explore it to understand the problem",
};

/** The one word each permission is called, everywhere, unchanged. */
export const PURPOSE_LABELS: Record<Purpose, string> = {
  benchmark: "Benchmark",
  training: "Training",
  exploration: "Exploration",
};
